import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18next from 'i18next';
import Cookies from 'js-cookie';
import { css } from '@emotion/core';
import { Box, Typography } from '@material-ui/core';
import ShareButton from 'components/common/Button/ShareButton';
import { system900, promo500 } from 'Mui/styles/color';
import { updateSettingStyle } from 'client/redux/modules/settingStyle';
import useGetReferralData from 'client/hooks/useGetReferralData';
import { resultFooterContainerStyle } from 'client/styles/common';
import { getShareInfoApi } from 'api/client';
import { defaultReferralShareCard, defaultReferralButton } from 'config/promoDefaultSettingValue';
import { replaceTextVariable } from 'utils/filter';

function ResultFooter({ propsReferralCount = 0 }) {
    const dispatch = useDispatch();

    const { uuid: promoUUID, id: promoId } = useSelector(s => s.promo);
    const { isMgmShare = false } = useSelector(s => s.flags);
    const { referralShareCard: styleReferralShareCard = {}, referralButton = {} } = useSelector(
        s => s.settingStyle
    );
    const { referralShareCard: mgmReferralShareCard = {} } = useSelector(s => s.settingMGMReferral);
    const entriesList = useSelector(s => s.userGameResultRecords);
    const { isLogin } = useSelector(s => s.userLoginState);
    const userGameResultRecords = useSelector(s => s.userGameResultRecords);
    const { currentEntryId = '' } = useSelector(s => s.userEntryState);
    const [enableFetchReferralInfo, setEnableFetchReferralInfo] = useState(false);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const latestEntry = entriesList[entriesList.length - 1];
    const { referralCode, referralUrl, referralCount, isLoading } = useGetReferralData({
        promoUUID,
        promoId,
        latestEntryId: latestEntry?.id,
        latestEntryUuid: latestEntry?.uuid,
        // dependency 都要是 true hook 才會在一載入時跑 useEffect 去打 referral api 拿 referralCode, referralUrl, referralCount
        dependency: [enableFetchReferralInfo]
    });
    const showReferralCount = referralCount > 0 || propsReferralCount > 0;

    const referralShareCard = useMemo(
        () =>
            Object.keys(mgmReferralShareCard).length > 0
                ? mgmReferralShareCard
                : styleReferralShareCard,
        [styleReferralShareCard, mgmReferralShareCard]
    );

    const config = {
        variant: 'contained',
        isFixedWindow: true,
        dialogTextColor: referralShareCard?.themeTextColor,
        dialogBgColor: referralShareCard?.themeBgColor,
        enableLineLiff: referralShareCard?.enableLineLiff,
        enableDisplayShareCardButton: isMgmShare,
        enableDisplayShareCardImage: referralShareCard?.isShowImage,
        enableDisplayShareCardReferralCode: false,
        shareCardTitle: referralShareCard?.invitationTitle,
        shareCardDesc: referralShareCard?.invitationDesc,
        shareCardImageUrl: referralShareCard?.imageUrl,
        shareCardButtonText: referralShareCard?.buttonText,
        shareCardButtonTextColor: referralShareCard?.buttonTextColor,
        shareCardButtonBgColor: referralShareCard?.buttonBgColor,
        isLoadingReferral: isLoading,
        referralCode,
        shareLinkUrl: referralUrl
    };

    const getShareInfoData = useCallback(async () => {
        try {
            setIsFetchingData(true);
            const { payload } = await getShareInfoApi({
                promoUUID,
                entryUUID: latestEntry?.uuid
            });
            const { shareMemberFullname } = payload;
            dispatch(
                updateSettingStyle({
                    referralShareCard: {
                        ...referralShareCard,
                        invitationTitle: replaceTextVariable(referralShareCard?.invitationTitle, {
                            username: shareMemberFullname
                        }),
                        invitationDesc: replaceTextVariable(referralShareCard?.invitationDesc, {
                            username: shareMemberFullname
                        }),
                        buttonText: replaceTextVariable(referralShareCard?.buttonText, {
                            username: shareMemberFullname
                        }),
                        fullname: shareMemberFullname || ''
                    }
                })
            );
        } catch (err) {
            console.error(err);
        } finally {
            setIsFetchingData(false);
        }
    }, [dispatch, latestEntry?.uuid, promoUUID, referralShareCard]);

    const handleShareButtonClick = () => {
        setEnableFetchReferralInfo(true);
        // 已經優先點擊分享邀請資訊dialog 就不再跳邀請提示
        const hasReferralNotifyEntry = JSON.parse(Cookies.get('hasReferralNotifyEntry') || '[]');
        const existEntryUuid = hasReferralNotifyEntry.find(
            entryId =>
                Boolean(userGameResultRecords.find(({ id }) => id === entryId)) ||
                currentEntryId === entryId
        );

        if (!existEntryUuid) {
            Cookies.set(
                'hasReferralNotifyEntry',
                JSON.stringify([...hasReferralNotifyEntry, currentEntryId])
            );
        }

        // 若活動登入設定匿名登入， 後端會回傳 fullname 等於 null，但打 API 前 fullname 是 undefined
        if (referralShareCard?.fullname === undefined && isLogin) {
            getShareInfoData();
        }
    };

    useEffect(() => {
        // [860t0duz4] 若 MGM phrase 2 前的建立的活動沒有邀請按鈕與邀請資訊的預設值，系統要幫他自動補上
        if (Object.keys(referralShareCard).length === 0) {
            dispatch(
                updateSettingStyle({
                    referralShareCard: {
                        ...referralShareCard,
                        ...defaultReferralShareCard(true).referralShareCard
                    }
                })
            );
        }
    }, [dispatch, referralShareCard]);

    useEffect(() => {
        // [860t0duz4] 若 MGM phrase 2 前的建立的活動沒有邀請按鈕與邀請資訊的預設值，系統要幫他自動補上
        if (Object.keys(referralButton).length === 0) {
            dispatch(
                updateSettingStyle({
                    referralButton: {
                        ...defaultReferralButton(true).referralButton
                    }
                })
            );
        }
    }, [dispatch, referralButton]);

    return (
        <div css={css(resultFooterContainerStyle)}>
            {referralButton?.isShowButton && (
                <Box css={buttonsWrapperStyle} py={1} px={2}>
                    <Typography
                        variant="h6"
                        css={css(shareButtonLabelTextStyle(referralButton?.buttonBgColor))}
                    >
                        {i18next.t('btn.referral_friend_join')}
                    </Typography>
                    <ShareButton
                        config={config}
                        bgColor={referralButton?.buttonBgColor}
                        text={referralButton?.buttonText}
                        textColor={referralButton?.buttonTextColor}
                        onClick={handleShareButtonClick}
                        isFetchingData={isFetchingData}
                    />
                    {showReferralCount && (
                        <Typography css={css(successReferralCountStyle)}>
                            {i18next.t('label.mgm_share.hint.success_join_count', {
                                count: propsReferralCount ?? referralCount
                            })}
                        </Typography>
                    )}
                </Box>
            )}
        </div>
    );
}

export default React.memo(ResultFooter);

const shareButtonLabelTextStyle = color => css`
    padding: 8px 16px 16px 16px;
    color: ${color || system900.color};
`;

const successReferralCountStyle = css`
    font-size: 12px;
    line-height: 17px;
    color: ${promo500.color};
    text-align: center;
    padding-top: 4px;
`;

const buttonsWrapperStyle = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;
